import React, { useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import useUsers from 'api/useUsers';
import { EFeatureFlag } from 'constants/enums';
import * as z from 'zod';

import { Form } from 'components/ui';

import usePortalSettings from '../../api/portal/usePortalSettings';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import { Tab, Tabs, TabsList } from '../../components/Tabs/Tabs';
import { TabsContent } from '../../components/ui/tabs';
import { EToasterStatus, showToast } from '../../utils/showToast';

import {
  buildParsedSettingsData,
  DEFAULT_COLOR,
  SETTINGS_FORM_SCHEMA
} from './constants';
import DesignLayoutSettings from './DesignLayoutSettings';
import LoginPopupSettings from './LoginPopupSettings';
import { SettingsFormData } from './types';

enum EPortalSettingsTabs {
  PORTAL_THEME = 'portal-theme',
  LOGIN_POPUP = 'login-popup'
}

const PORTAL_SETTINGS_TABS: Tab[] = [
  {
    id: EPortalSettingsTabs.PORTAL_THEME,
    value: 'Portal Theme'
  },
  {
    id: EPortalSettingsTabs.LOGIN_POPUP,
    value: 'Login Popup'
  }
];

const BuilderView = () => {
  const { getPortalSettingsData, editPortalSettings } = usePortalSettings();
  const currentSettingsData = getPortalSettingsData?.data ?? {};
  const {
    theme: {
      general = {},
      header = {},
      sidebar = {},
      footer = {},
      loginPopup = {}
    } = {},
    seo = {}
  } = currentSettingsData;

  const { fetchFeatureFlags } = useUsers({});

  const hasFeatureFlagShowLoginButton =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_SHOW_LOGIN_BUTTON_PORTAL_STUDIO
    ];

  const initialValues = useMemo(
    () => ({
      generalBackgroundColor:
        general?.backgroundColor?.colorOne || DEFAULT_COLOR,
      generalFavicon: general?.portalFavIcon || '',
      headerLogo: header?.logo || '',
      headerBackgroundColor: header?.backgroundColor?.colorOne,
      headerTextColor: header?.textColor || DEFAULT_COLOR,
      headerIsPlayerNameShown: header?.showPlayerName,
      headerIsPlayerBalanceShown: header?.showPlayerBalance,
      headerIsLoginBtnShown: hasFeatureFlagShowLoginButton
        ? header?.showLogin
        : true,
      headerIsLanguageSwitcherShown: header?.showTranslations,
      seoTitle: seo?.portalTitle || '',
      seoDescription: seo?.portalDescription || '',
      seoKeywords: seo?.portalKeywords ?? [],
      seoImg: seo?.portalOGImage || '',
      sidebarBackgroundColor:
        sidebar?.backgroundColor?.colorOne || DEFAULT_COLOR,
      sidebarTextColor: sidebar?.textColor || DEFAULT_COLOR,
      sidebarHoverColor: sidebar?.tabHoverColor?.colorOne || DEFAULT_COLOR,
      footerBackgroundColor: footer?.backgroundColor?.colorOne || DEFAULT_COLOR,
      footerTextColor: footer?.textColor || DEFAULT_COLOR,
      footerPrivacyLink: footer?.privacyPolicyUrl || '',
      footerTermsConditionsLink: footer?.termsOfServiceUrl || '',
      footerSupportLink: footer?.supportUrl || '',
      loginPopupText: loginPopup?.text || '',
      loginPopupFont: loginPopup?.font || 'Roboto',
      loginPopupFontSize: loginPopup?.fontSize || 12,
      loginPopupFontColor: loginPopup?.fontColor || DEFAULT_COLOR,
      loginPopupFontWeight: loginPopup?.fontWeight || 'regular',
      loginPopupBackgroundImageMobile: loginPopup?.backgroundImageMobile || '',
      loginPopupBackgroundImageDesktop:
        loginPopup?.backgroundImageDesktop || '',
      loginPopupLogo: loginPopup?.logo || '',
      loginPopupLogoSize: loginPopup?.logoSize || '',
      loginPopupConsentSection: {
        htmlText: loginPopup.consentSection?.htmlText || '',
        consentRequired: loginPopup.consentSection?.consentRequired || false
      }
    }),
    [currentSettingsData, hasFeatureFlagShowLoginButton]
  );

  const settingsFormConfig = useForm<z.infer<typeof SETTINGS_FORM_SCHEMA>>({
    resolver: zodResolver(SETTINGS_FORM_SCHEMA),
    defaultValues: initialValues,
    mode: 'onChange',
    shouldFocusError: true
  });

  const {
    control,
    reset,
    watch,
    handleSubmit,
    formState: { isValid, isDirty, errors }
  } = settingsFormConfig;

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  useEffect(() => {
    if (location.pathname.endsWith('/builder')) {
      navigate('portal-theme', { replace: true });
    }
  }, [location.pathname, navigate]);

  const onSettingsFormSubmit = useCallback(
    async (data: SettingsFormData) => {
      const parsedSettingsData = buildParsedSettingsData(data);

      try {
        await editPortalSettings.mutateAsync(parsedSettingsData);
        showToast({
          message: 'Portal settings were successfully updated',
          status: EToasterStatus.SUCCESS
        });
      } catch (error: any) {
        showToast({
          message:
            error?.response?.data?.message ||
            'Portal settings were not updated. Please try again',
          status: EToasterStatus.ERROR
        });
      }
    },
    [editPortalSettings]
  );

  return (
    <div className="overflow-scroll">
      <Form {...settingsFormConfig}>
        <form onSubmit={handleSubmit(onSettingsFormSubmit)}>
          <PortalHeader
            title="Portal Settings"
            primaryButtonText="Save"
            secondaryButtonVariant="outline"
            secondaryButtonIcon={<FontAwesomeIcon icon="fa-regular fa-eye" />}
            primaryButtonState={{ disabled: !isValid || !isDirty }}
          />
          <div className="w-full px-7">
            <Tabs
              defaultValue={EPortalSettingsTabs.PORTAL_THEME}
              onValueChange={(value) => {
                navigate(value);
              }}
            >
              <TabsList tabs={PORTAL_SETTINGS_TABS} />

              <TabsContent
                value={EPortalSettingsTabs.PORTAL_THEME}
                className="mt-5"
              >
                <DesignLayoutSettings
                  control={control}
                  errors={errors}
                  hasFeatureFlagShowLoginButton={hasFeatureFlagShowLoginButton}
                />
              </TabsContent>

              <TabsContent
                value={EPortalSettingsTabs.LOGIN_POPUP}
                className="mt-5"
              >
                <LoginPopupSettings
                  control={control}
                  errors={errors}
                  watch={watch}
                  initialFontSize={initialValues.loginPopupFontSize}
                />
              </TabsContent>
            </Tabs>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BuilderView;
